import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { buildMuiTheme } from "../Utils/muiTheme";

// Keep the key, the attribute names and the "anything else means system"
// rule in step with the inline script in public/index.html, which applies
// the same logic before React loads so the first paint is already themed.
export const THEME_STORAGE_KEY = "hc-theme-mode";
export const THEME_MODES = ["system", "light", "dark"];
const DARK_QUERY = "(prefers-color-scheme: dark)";

const ThemeContext = createContext({
  mode: "system",
  resolvedMode: "light",
  setMode: () => {},
});

const getStoredMode = () => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : "system";
  } catch {
    return "system";
  }
};

const getSystemMode = () =>
  typeof window !== "undefined" && window.matchMedia?.(DARK_QUERY).matches ? "dark" : "light";

const resolveMode = (mode, systemMode) => (mode === "system" ? systemMode : mode);

// data-theme drives our own tokens (src/Styles/tokens), data-bs-theme drives
// Bootstrap's. Written straight to <html> rather than from an effect so the
// CSS variables already hold the new theme's values when React re-renders —
// muiTheme.js and Utils/theme.js read them with getComputedStyle.
const applyResolvedMode = (resolvedMode) => {
  const root = document.documentElement;
  root.setAttribute("data-theme", resolvedMode);
  root.setAttribute("data-bs-theme", resolvedMode);
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => {
    const initialMode = getStoredMode();
    applyResolvedMode(resolveMode(initialMode, getSystemMode()));
    return initialMode;
  });
  const [systemMode, setSystemMode] = useState(getSystemMode);
  const modeRef = useRef(mode);

  const setMode = useCallback((nextMode) => {
    const safeMode = THEME_MODES.includes(nextMode) ? nextMode : "system";
    try {
      if (safeMode === "system") {
        localStorage.removeItem(THEME_STORAGE_KEY);
      } else {
        localStorage.setItem(THEME_STORAGE_KEY, safeMode);
      }
    } catch {
      // Storage unavailable (private mode / blocked): the choice still
      // applies for this session, it just won't be remembered.
    }
    modeRef.current = safeMode;
    applyResolvedMode(resolveMode(safeMode, getSystemMode()));
    setModeState(safeMode);
  }, []);

  // Follow the OS while the user is on "system".
  useEffect(() => {
    const media = window.matchMedia?.(DARK_QUERY);
    if (!media) return undefined;

    const handleChange = (event) => {
      const nextSystemMode = event.matches ? "dark" : "light";
      applyResolvedMode(resolveMode(modeRef.current, nextSystemMode));
      setSystemMode(nextSystemMode);
    };

    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);

  // Keep other open tabs of the app on the same choice.
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== THEME_STORAGE_KEY && event.key !== null) return;
      const nextMode = getStoredMode();
      modeRef.current = nextMode;
      applyResolvedMode(resolveMode(nextMode, getSystemMode()));
      setModeState(nextMode);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const resolvedMode = resolveMode(mode, systemMode);
  const value = useMemo(() => ({ mode, resolvedMode, setMode }), [mode, resolvedMode, setMode]);
  const muiTheme = useMemo(() => buildMuiTheme(resolvedMode), [resolvedMode]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// mode: what the user picked ("system" | "light" | "dark").
// resolvedMode: what is actually on screen ("light" | "dark").
export const useThemeMode = () => useContext(ThemeContext);

// For components that build ECharts options. ECharts draws to canvas, so its
// colors are plain strings read from the CSS tokens when the option object is
// built. Calling this re-renders the component on a theme change, which
// rebuilds options created during render; add the returned value to the
// dependency list of any option that is wrapped in useMemo. Nothing is
// refetched or remounted.
export const useChartTheme = () => useContext(ThemeContext).resolvedMode;
